import { FetchResult, MutationResult, gql, useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { logger } from '../../../datadog';
import { withRetry } from '../../../utils';

const CREATE_VISITOR_CHAT_EXPERIENCE = gql`
  mutation CreateTwilioVisitorChatExperience($providerShortId: String!, $visitorName: String!, $visitorId: String!) {
    createTwilioVisitorChatAccess(visitorName: $visitorName, visitorId: $visitorId) {
      jwt
    }
    createTwilioWaitingRoomConversation(
      providerShortId: $providerShortId
      visitorUuid: $visitorId
      visitorName: $visitorName
    ) {
      conversationSid
    }
  }
`;

type CreateVisitorChatExperienceMutationData = {
  createTwilioVisitorChatAccess: { jwt: string };
  createTwilioWaitingRoomConversation: {
    conversationSid: string;
  };
};

type CreateVisitorChatExperienceMutationVariables = {
  providerShortId: string;
  visitorName: string;
  visitorId: string;
};

export function useCreateVisitorChatExperienceMutation() {
  return useMutation<CreateVisitorChatExperienceMutationData, CreateVisitorChatExperienceMutationVariables>(
    CREATE_VISITOR_CHAT_EXPERIENCE,
    { context: { skipAuth: true } },
  );
}

type CreateVisitorChatExperienceCallback = (
  providerShortId: string,
  visitorName: string,
  visitorId: string,
) => Promise<FetchResult<CreateVisitorChatExperienceMutationData> | undefined>;

/**
 * Chat is non-blocking for room join — terminal failures are logged + swallowed
 * (see callback below). A few attempts with backoff to ride out transient
 * Twilio Sync errors.
 */
const VISITOR_CHAT_EXPERIENCE_RETRY_CONFIG = {
  retries: 3,
  initialDelayMs: 500,
  backoffFactor: 2,
};

export function useCreateVisitorChatExperienceCallback(): [
  CreateVisitorChatExperienceCallback,
  MutationResult<CreateVisitorChatExperienceMutationData>,
] {
  const [createVisitorChatExperience, result] = useCreateVisitorChatExperienceMutation();

  const createVisitorChatExperienceWithRetry = useMemo(
    () => withRetry(createVisitorChatExperience, VISITOR_CHAT_EXPERIENCE_RETRY_CONFIG),
    [createVisitorChatExperience],
  );

  const createVisitorChatExperienceCallback = useCallback(
    async (providerShortId: string, visitorName: string, visitorId: string) => {
      try {
        return await createVisitorChatExperienceWithRetry({
          variables: {
            providerShortId,
            visitorName,
            visitorId,
          },
        });
      } catch (error) {
        logger.error(
          'Unable to set up waiting room chat',
          {
            providerShortId,
            visitorId,
          },
          error as Error,
        );
      }
    },
    [createVisitorChatExperienceWithRetry],
  );

  return [createVisitorChatExperienceCallback, result];
}
